import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewStaffMember, editStaffMember, getStaffMembers } from "../../../redux/features/staff/staffService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";
import Dropdown from "../../../components/Dropdown/Dropdown";

const Roles = [{ id: '1', name: 'customer_specialist' }, { id: '2', name: 'sales_person' }, { id: '3', name: 'manager' }]

function formatLabel(str) {
    return str
        .split('_')
        .map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(' ');
}

const Staff = () => {

    const dispatch = useDispatch();
    const roleDropdownRef = useRef(null);

    const { isLoading, pageCount, staff } = useSelector((state) => state.staff.staffMembers);
    const { isStaffMemberCreating } = useSelector((state) => state.staff.newStaffMember);
    const { isEditingStaffMember } = useSelector((state) => state.staff.editedStaffMember);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
        whatsapp_number: '',
        role: ''
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedStaffMember, setSelectedStaffMember] = useState(null);
    const [initialStaffMember, setInitialStaffMember] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        staffMember_code: ''
    });
    const [selectedRole, setSelectedRole] = useState({
        id: '',
        name: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllStaffMembers = useCallback(() => {
        dispatch(getStaffMembers({
            skip: skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllStaffMembers();
    }, [fetchAllStaffMembers]);

    const handleSelect = (option) => {
        setFormData({ ...formData, role_name: option.name });
        setErrorMessages({ ...errorMessages, role: "" });
        setSelectedRole({
            id: option.id,
            name: option.name
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidStaffMember = () => {
        const errors = {};
        const trimmedName = formData.full_name.trim();
        if (!trimmedName) {
            errors.full_name = "Name is required.";
        }
        if (!formData.email) {
            errors.email = "Email is required.";
        }
        if (!formData.phone_number) {
            errors.phone_number = "Phone Number is required.";
        }
        if (!formData.whatsapp_number) {
            errors.whatsapp_number = "WhatsApp Number is required.";
        }
        if (!formData.role_name) {
            errors.role = "Role is required.";
        }
         if (!formData.password) {
            errors.password = "Password is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewStaffMember = async () => {
        if (isValidStaffMember()) {
            setBtnDisable(true);
            try {
                if (formData.role_name === 'Customer Specialist') {
                    formData.role_name = 'customer-specialist'
                }
                if (formData.role_name === 'Sales Person') {
                    formData.role_name = 'sales-person'
                }
                if (formData.role_name === 'Manager') {
                    formData.role_name = 'manager'
                }
                const response = await dispatch(addNewStaffMember(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                    staffMember_code: ''
                });
                fetchAllStaffMembers();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit staffMember
    const handleSelectedStaffMemberChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedStaffMember((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditStaffMember = () => {
        const errors = {};
        const trimmedName = selectedStaffMember.full_name.trim();

        if (!trimmedName) {
            errors.full_name = "Name is required.";
        }
        if (!selectedStaffMember.email) {
            errors.email = "Email is required.";
        }
        if (!selectedStaffMember.phone_number) {
            errors.phone_number = "Phone Number is required.";
        }
        if (!selectedStaffMember.whatsapp_number) {
            errors.whatsapp_number = "WhatsApp Number is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialStaffMember[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = +selectedData[key];
            }
        }
        return changedValues;
    }

    const editStaffMemberDetails = async () => {
        if (isValidEditStaffMember()) {
            const data = getChangedValues(selectedStaffMember);
            const hasChanges = Object.keys(data).some(key => key !== 'id');

            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editStaffMember(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedStaffMember(null);
                    setInitialStaffMember(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllStaffMembers();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate StaffMember
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedStaffMemberId, setSelectedStaffMemberId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editStaffMember({ id: selectedStaffMemberId, status: true })).unwrap();
            setSelectedStaffMemberId(null);
            setInitialStaffMember(null);
            showToast('success', response.message);
            fetchAllStaffMembers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editStaffMember({ id: selectedStaffMemberId, status: false })).unwrap();
            setSelectedStaffMemberId(null);
            setInitialStaffMember(null);
            showToast('success', response.message);
            fetchAllStaffMembers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Staff Member</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='full_name'
                                        name='full_name'
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.full_name && (
                                        <p className={`text-danger`}>{errorMessages.full_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Email Address<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='email'
                                        name='email'
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder='Enter Email'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.email && (
                                        <p className={`text-danger`}>{errorMessages.email}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Phone Number<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='phone_number'
                                        name='phone_number'
                                        value={formData.phone_number}
                                        onChange={handleInputChange}
                                        placeholder='Enter Phone Number'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.phone_number && (
                                        <p className={`text-danger`}>{errorMessages.phone_number}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        WhatsApp Number<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='whatsapp_number'
                                        name='whatsapp_number'
                                        value={formData.whatsapp_number}
                                        onChange={handleInputChange}
                                        placeholder='Enter Phone Number'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.whatsapp_number && (
                                        <p className={`text-danger`}>{errorMessages.whatsapp_number}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Role<span className={`required-star`}>*</span>
                                    </label>
                                    <Dropdown
                                        ref={roleDropdownRef}
                                        options={Roles.map(role => ({
                                            ...role,
                                            name: role.name
                                                .split('_')
                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(' ')
                                        }))}
                                        placeholder="Choose an Option"
                                        onSelect={handleSelect}
                                        value={selectedRole.name}
                                    />
                                    {errorMessages.role && (
                                        <p className={`text-danger`}>{errorMessages.role}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Password<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='password'
                                        id='password'
                                        name='password'
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder='Enter Password'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.password && (
                                        <p className={`text-danger`}>{errorMessages.password}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewStaffMember}
                        >
                            {isStaffMemberCreating ? <BeatLoader staffMember='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Staff Members</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isLoading ?
                        <TableSkeleton columns={["Full Name", "Email", "Role", "Status", "Actions"]} rowCount={10} height={20} />
                        : staff.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Full Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {staff?.map((staffMember) => (
                                            <tr key={staffMember.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {staffMember.full_name}
                                                </td>
                                                <td>{staffMember.email}</td>
                                                <td>{formatLabel(staffMember.role.name)}</td>
                                                <td>{staffMember.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedStaffMember(staffMember);
                                                        setInitialStaffMember(staffMember);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            staffMember_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={staffMember.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedStaffMemberId(staffMember.id);
                                                            staffMember.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{staffMember.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit StaffMember</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedStaffMember.full_name || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter StaffMember Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.full_name && (
                                                <p className={`text-danger`}>{editErrorMsgs.full_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Email<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='email'
                                                name='email'
                                                value={selectedStaffMember.email || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter Email'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.email && (
                                                <p className={`text-danger`}>{editErrorMsgs.email}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Phone Number<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='phone_number'
                                                name='phone_number'
                                                value={selectedStaffMember.phone_number || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter Phone Number'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.phone_number && (
                                                <p className={`text-danger`}>{editErrorMsgs.phone_number}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                WhatsApp Number<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='whatsapp_number'
                                                name='whatsapp_number'
                                                value={selectedStaffMember.whatsapp_number || ''}
                                                onChange={handleSelectedStaffMemberChange}
                                                placeholder='Enter WhatsApp Number'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.whatsapp_number && (
                                                <p className={`text-danger`}>{editErrorMsgs.whatsapp_number}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editStaffMemberDetails}
                                    >
                                        {isEditingStaffMember ? <BeatLoader staffMember='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='StaffMember Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the StaffMember?`}
                    staffMemberType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='StaffMember Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the StaffMember?`}
                    staffMemberType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Staff;
